import { Checkbox, Typography } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import React, { memo, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import NewTabLink from 'components/atoms/NewTabLink';
import DefaultForm from 'components/composites/DefaultForm';
import BackOfficeDrawer from 'components/drawers/BackOfficeDrawer';
import IPCameraAddFormItems from 'components/forms/IPCameraAddFormItems';
import { INSUFFICIENT_PERMISSIONS_MESSAGE } from 'constants/internal-admin-roles';
import { SEARCH_PARAMS } from 'constants/search-params';
import clearErrors from 'store/modules/error/actions';
import { createIPCamera } from 'store/modules/integration/ipCameras/actions';
import ActionTypes from 'store/modules/integration/ipCameras/constants';
import { useMyUserHasInternalPermission } from 'store/modules/myUser/hooks';
import { red } from 'style/colour-palette';
import { ProtectedResource, ResourceAction } from 'types/action.enum';
import { CreateIPCameraDto } from 'types/dto/ip-camera.dto';

const actions = [ActionTypes.CREATE_IP_CAMERA_REQUEST];

type IPCameraAddFormValues = CreateIPCameraDto & {
  enterpriseId: number | null;
  cameraOnlineCheck: boolean;
};

function BackOfficeIPCameraAddDrawer(): JSX.Element | null {
  const [form] = useForm<IPCameraAddFormValues>();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const drawer = searchParams.get(SEARCH_PARAMS.DRAWER.key);
  const visible = drawer === SEARCH_PARAMS.DRAWER.values.ADD_IP_CAMERA;
  const [isSheetChecked, setIsSheetChecked] = useState(false);
  const [isFirmwareChecked, setIsFirmwareChecked] = useState(false);

  const canProvisionIPCameras = useMyUserHasInternalPermission({
    resource: ProtectedResource.IP_CAMERA,
    action: ResourceAction.ADD
  });

  const handleClose = () => {
    setSearchParams((prev) => {
      const newSearchParams = new URLSearchParams(prev);
      newSearchParams.delete(SEARCH_PARAMS.DRAWER.key);
      return newSearchParams;
    });
  };

  const submitFormAndCloseDrawer = () => {
    form
      .validateFields()
      .then(values => {
        const { enterpriseId, cameraOnlineCheck, ...newIPCamera } = values;
        dispatch(createIPCamera(newIPCamera, handleClose));
      })
      .catch(() => {
        // Do nothing, errors are displayed by the form
      });
  };

  useEffect(() => {
    if (visible) dispatch(clearErrors(actions));
  }, [dispatch, visible]);

  if (!canProvisionIPCameras) {
    return (
      <BackOfficeDrawer
        visible={visible}
        backOfficeSection="Add IP Camera"
        actions={[]}
        noFooter
        handleClose={handleClose}
      >
        {INSUFFICIENT_PERMISSIONS_MESSAGE}
      </BackOfficeDrawer>
    );
  }

  const isPrerequisitesMet = isSheetChecked && isFirmwareChecked;
  return (
    <BackOfficeDrawer
      visible={visible}
      backOfficeSection="Add IP Camera"
      actions={actions}
      handleClose={handleClose}
      handleSave={submitFormAndCloseDrawer}
      noFooter={!isPrerequisitesMet}
    >
      <Typography.Paragraph>
        Ensure the pre-requisites and firmware versions are met before filling out this form. See the{' '}
        <NewTabLink
          text="checklist"
          href="https://farmbot-au.atlassian.net/wiki/x/AQDBq"
        />
      </Typography.Paragraph>
      <div className="mb-8">
        <Checkbox onChange={e => setIsSheetChecked(e.target.checked)}>
          I have checked the spreadsheet
        </Checkbox>
      </div>
      <div className="mb-16">
        <Checkbox onChange={e => setIsFirmwareChecked(e.target.checked)}>
          I have checked the firmware version and it is compatible with MyFarmbot. I understand{' '}
          <span style={{ color: red }}>firmware builds older than May 2024 are incompatible</span>.
        </Checkbox>
      </div>
      <DefaultForm
        form={form}
        layout="vertical"
        name="addIPCamera"
        requiredMark="optional"
        disabled={!isPrerequisitesMet}
      >
        <IPCameraAddFormItems />
      </DefaultForm>
    </BackOfficeDrawer>
  );
}

export default memo(BackOfficeIPCameraAddDrawer);
